<template>
  <button :class="['gam-button', size, getClasses]" @click="emitButtonEvent" :data-test-id="data?.testId">
    <div :class="['gam-button-container', getContainerClasses, containerClass]">
      <div class="gam-button-wrapper">
        <div v-if="getLeftIcon" class="icon icon-left">
          <gam-icon v-bind="getLeftIcon" @click="clickIconLeft" />
        </div>

        <div v-if="!(isIconOnly && leftIcon)" class="content" :class="{ sub: subLabel }">
          <div v-if="getChipProps" class="chip">
            <gam-chip v-bind="getChipProps" />
          </div>

          <div v-if="!label" class="label">
            <slot />
          </div>

          <div v-if="label" class="label" v-html="localize(label)" />

          <div v-if="subLabel" class="sub-label" v-html="localize(subLabel)" />
        </div>

        <div v-if="getRightIcon && !isIconOnly" class="icon icon-right">
          <gam-icon v-bind="getRightIcon" @click="clickIconRight" />
        </div>
      </div>
    </div>
  </button>
</template>

<script lang="ts" setup>
import { localize } from '@/core/gambits';
import GamChip from '@/views/components/GamChip.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamChipSize } from '@/views/composables/constants/components/gamChip.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamChipType } from '@/views/composables/models/components/GamChip';
import type { GamIconType } from '@/views/composables/models/components/GamIcon';
import { computed } from 'vue';

const props = withDefaults(defineProps<GamButtonType>(), {
  size: GamButtonSize.LARGE,
  variant: GamButtonVariant.PRIMARY,
});

const isPrimary = (): boolean => {
  if (props.isIconPrimary) return true;
  return !props.isIconWhite && props.variant === GamButtonVariant.TERTIARY && props.size !== GamButtonSize.ACTIONS;
};

const getIconValues = (name: GamIconName): GamIconType => {
  return {
    name,
    isDark: props.variant === GamButtonVariant.PRIMARY,
    isPrimary: isPrimary(),
  };
};

const getClasses = computed(() => ({
  skeleton: props.isSkeleton,
  'full-size': props.isFullSize,
  'dark-bg': props.variant === GamButtonVariant.HYPE,
}));

const getContainerClasses = computed(() => ({
  'is-icon': props.isIconOnly,
  'is-disabled': props.isDisabled,
  'is-loading': props.isLoading,
  'is-active': props.isActive,
  'is-centered': props.isCentered,
  'is-primary': props.variant === GamButtonVariant.PRIMARY,
  'is-secondary': props.variant === GamButtonVariant.SECONDARY,
  'is-tertiary': props.variant === GamButtonVariant.TERTIARY,
  'is-afforded': props.variant === GamButtonVariant.AFFORDED,
  'is-slab': props.variant === GamButtonVariant.SLAB,
  'is-steklo': props.variant === GamButtonVariant.STEKLO,
  'is-hype': props.variant === GamButtonVariant.HYPE,
  'is-drop-shadow': props.isDropShadow,
  'is-deep-shadow': props.isDeepShadow,
  'with-chip': props.chip,
  flag: !!props.flagIcon,
  left: !props.leftIcon && !props.flagIcon,
  right: !props.rightIcon,
}));

const getChipProps = computed((): GamChipType | undefined => {
  if (props.chip) {
    return {
      ...props.chip,
      size: props.size === GamButtonSize.LARGE ? GamChipSize.NORMAL : GamChipSize.SMALL,
    };
  }

  return undefined;
});

const getLeftIcon = computed((): GamIconType | undefined => {
  if (props.leftIcon) {
    return getIconValues(props.leftIcon);
    // } else if (props.flagIcon) {
    //   return {
    //     name: props.flagIcon,
    //     isFlag: true,
    //     size: GamIconSize.NORMAL,
    //   };
  }

  return undefined;
});

const getRightIcon = computed((): GamIconType | undefined => {
  if (props.rightIcon) {
    return getIconValues(props.rightIcon);
  }

  return undefined;
});

const clickIconLeft = (event: MouseEvent) => {
  if (props.callbackIconLeft) {
    event?.preventDefault();
    props.callbackIconLeft();
  }
};

const clickIconRight = (event: MouseEvent) => {
  if (props.callbackIconRight) {
    event?.preventDefault();
    props.callbackIconRight();
  }
};

const emitButtonEvent = (event: MouseEvent): void => {
  if (props.isDisabled || typeof props.onClick !== 'function') return;

  event.preventDefault();

  // TODO: Temporary test augmentation
  // @ts-ignore
  const isAutomationEnvironment = window.AUTOMATION_overrideConfirmations === true;
  if (!isAutomationEnvironment && props.confirmation && !window.confirm(localize(props.confirmation))) {
    return;
  }

  props.onClick(event);
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

$large-size: 47px;
$small-size: 34px;
$x-small-size: 24px;
$tiny-size: 18px;

.gam-button-container.is-drop-shadow {
  box-shadow: var(--shadow-button-action);
}

.gam-button-container.is-deep-shadow {
  box-shadow: var(--shadow-button-deep);
}

.gam-button {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 12px;
  position: relative;
  touch-action: manipulation;
  background: transparent;
  color: var(--color-white-100);
  border-width: 0;
  padding: 0;
  font-family: 'Hanken Grotesk', sans-serif;
  flex-shrink: 0;
  user-select: none;

  .gam-button-wrapper,
  .label {
    width: 100%;
  }

  &.dark-bg {
    background: var(--color-dark-900);
    border-radius: var(--radius-large);
  }

  &.full-size {
    align-self: stretch;
    flex: 1 0 0;
    width: var(--button-full-width, 100%);

    .gam-button-container {
      justify-content: flex-start;

      &.is-icon {
        width: 100% !important;
      }
    }

    &.large {
      .gam-button-container {
        &.is-icon {
          width: 100% !important;
        }
      }
    }
  }

  &.large {
    .icon,
    .label {
      transform: translateY(-1px);
    }

    .gam-button-container {
      box-sizing: border-box;
      padding: 0 var(--spacing-xxl);
      // min-width: $large-size;
      min-height: $large-size;

      &.flag {
        padding-left: calc(var(--spacing-xxl) + 6px);
      }

      .gam-button-wrapper {
        .content {
          min-height: 26px;

          .label {
            font-size: 16px;
          }
        }
      }

      .icon {
        &-left {
          left: 16px;
        }

        &-right {
          right: 16px;
        }
      }

      &.is-primary {
        &:before {
          background: var(--color-linear-gradient);
          opacity: 0.7;
          inset: -5px;
          padding: 5px;
        }

        &.is-loading,
        &.is-active {
          &:before {
            opacity: 1;
            inset: -2px;
            padding: 2px;
          }
        }

        @include hover {
          &:before {
            opacity: 1;
            inset: -2px;
            padding: 2px;
          }
        }

        &.is-loading {
          background: var(--color-linear-gradient-skeleton);
          background-size: 200% 200%;
          animation: moving-bg 2s linear infinite;
          color: var(--color-white);
          opacity: 0.75;
          filter: saturate(0.75);
        }
      }

      &.is-icon {
        width: $large-size !important;
        height: $large-size !important;
      }
    }
  }

  &.small {
    .icon,
    .label {
      transform: translateY(-0.5px);
    }

    .gam-button-container {
      // min-width: $small-size;
      min-height: $small-size;

      // .label {
      //   line-height: 16px;
      // }

      &.is-primary {
        margin: 3px;

        &:before {
          background: var(--color-linear-gradient);
          opacity: 0.7;
          inset: -3px;
          padding: 3px;
        }
      }
    }

    .label {
      letter-spacing: 0.033em;
    }
  }

  &.actions,
  &.x-small {
    .gam-button-container {
      // min-width: $small-size;
      min-height: $small-size;

      &:before {
        background: var(--color-white-100) !important;
        opacity: 0 !important;
      }

      &.is-primary {
        &.is-active {
          background-color: var(--color-white-70);

          &:before {
            opacity: 1 !important;
          }
        }

        @include hover {
          background-color: var(--color-white-70);

          &:before {
            opacity: 1 !important;
          }
        }

        &.is-loading {
          background: var(--color-linear-gradient-skeleton);
          background-size: 200% 200%;
          animation: moving-bg 2s linear infinite;
          color: var(--color-white);
          opacity: 0.75;
          filter: saturate(0.75);
        }
      }

      &.is-secondary {
        background-color: var(--color-white-5);
        border: 1px solid var(--color-pink);

        &.is-active {
          background-color: var(--color-white-10);
        }

        @include hover {
          background-color: var(--color-white-10);
        }

        &.is-loading {
          background: var(--color-linear-gradient-skeleton);
          background-size: 200% 200%;
          animation: moving-bg 2s linear infinite;
          color: var(--color-white);
          opacity: 0.75;
          filter: saturate(0.75);
        }
      }

      &.with-chip {
        padding: 0 var(--spacing-xl);
      }
    }
  }

  &.x-small {
    .gam-button-container {
      // min-width: $x-small-size;
      min-height: $x-small-size;

      &.is-icon {
        width: $x-small-size !important;
        height: $x-small-size !important;
      }
    }

    .label {
      letter-spacing: 0.03em;
    }
  }

  &.tiny {
    font-size: 12px !important;
  }
}

.gam-button-container {
  width: 100%;
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--radius-large);
  padding: 0 var(--spacing-xl);
  cursor: pointer;
  position: relative;
  overflow: visible;
  transition: all var(--transition);
  // border: 1px solid var(--color-pink);

  &:not(.is-disabled):not(.is-loading) {
    @include press;
  }

  &.left {
    padding-left: var(--spacing-l);
  }

  &.right {
    padding-right: var(--spacing-l);
  }

  .gam-button-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    // min-width: 0;

    .content {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 20px;
      gap: var(--spacing-xs);

      &.sub {
        align-items: flex-start;
        flex-direction: column;
      }

      .label {
        font-size: 14px;
        font-weight: 700;
        overflow: hidden;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
        line-clamp: 1;
        text-overflow: ellipsis;
        align-content: center;
        word-break: break-all;
        padding: 1px 0;

        span {
          color: var(--color-white-30);
        }
      }

      .label,
      .sub-label {
        text-align: left;
      }

      .sub-label {
        font-size: 12px;
        font-weight: 400;
        color: var(--color-white-70);
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: left;
        white-space: pre;
        width: 100%;
      }
    }
  }

  .gam-button-load {
    position: absolute;
    visibility: hidden;

    .load-icon {
      animation: loader 1000ms var(--cubic-bezier) infinite;

      @keyframes loader {
        0% {
          rotate: 0deg;
        }
        100% {
          rotate: 360deg;
        }
      }
    }
  }

  .icon {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;

    &-left {
      left: 10px;
    }

    &-right {
      right: 8px;
    }
  }

  &.is-centered {
    justify-content: center !important;

    .gam-button-wrapper {
      justify-content: center;

      .content .label,
      .content .sublabel {
        text-align: center;
      }
    }
  }

  &.is-primary {
    background-color: var(--color-white-100);
    color: var(--color-dark-700);
    border: 3px solid var(--color-pink);

    @include hover {
      // background-color: var(--color-white-70);
      border: 5px solid var(--color-pink);
    }
  }

  &.is-icon.is-primary {
    border: none;
  }

  &.is-secondary {
    -webkit-backdrop-filter: blur(15px);
    backdrop-filter: blur(15px);
    background-color: var(--color-dark-5);
    border: 1px solid var(--color-pink);

    &.is-disabled,
    &.is-loading {
      border: 1px solid var(--color-white-50);
      color: var(--color-white-50);
    }

    &:before {
      background: var(--color-linear-gradient);
      opacity: 0.7;
    }

    &.is-active {
      background-color: var(--color-dark-500);
      &:before {
        opacity: 1;
      }
    }

    @include hover {
      background-color: var(--color-dark-500);
      &:before {
        opacity: 1;
      }
    }

    .label {
      -webkit-font-smoothing: antialiased;
    }
  }

  &.is-tertiary {
    color: var(--color-white-100);
    background-color: transparent !important;

    &.is-active {
      opacity: 1;
      color: var(--color-pink);
    }

    @include hover {
      opacity: 1;
      color: var(--color-pink);
    }
  }

  &.is-afforded {
    background: var(--color-dark-50);
    background: transparent;
    border: 1px solid var(--color-white-20);
    backdrop-filter: blur(15px);

    @include hover {
      background: var(--color-dark-500);
      border-color: var(--color-white-30);
      border-color: var(--color-pink);
    }
  }

  &.is-slab {
    // background: var(--color-white-10) !important;
    background: var(--color-fabric-gradient) !important;
    backdrop-filter: blur(15px);

    @include hover {
      // background: var(--color-dark-500) !important;
      // background: var(--color-dark-700) !important;
      background: var(--color-white-15) !important;
    }
  }

  &.is-steklo {
    background: var(--color-white-5);
    backdrop-filter: blur(15px);

    @include hover {
      background: var(--color-white-20);
    }
  }

  &.is-hype {
    -webkit-backdrop-filter: blur(15px);
    backdrop-filter: blur(15px);
    background: var(--color-linear-gradient-30), rgba(0, 0, 0, 1) !important;

    @include hover {
      background: var(--color-linear-gradient-30), rgba(255, 255, 255, 0.25) !important;
    }
  }

  &.is-hype.is-disabled {
    background: var(--color-linear-gradient-30), rgba(0, 0, 0, 25) !important;
    opacity: 0.75;
    filter: saturate(0.75);
  }

  &.is-hype.is-loading {
    background: var(--color-linear-gradient-skeleton);
    background-size: 200% 200%;
    animation: moving-bg 2s linear infinite;
    color: var(--color-white);
    opacity: 0.75;
    filter: saturate(0.75);
  }

  &.is-icon {
    width: $small-size !important;
    height: $small-size !important;
    padding-left: 0 !important;
    padding-right: 0 !important;

    .icon {
      position: relative !important;
      left: 0 !important;
    }
  }

  &.is-disabled,
  &.is-loading {
    cursor: default;
    -webkit-user-select: none;
    user-select: none;
    pointer-events: none;
  }

  &:not(.is-primary).is-disabled,
  &:not(.is-primary).is-loading {
    color: var(--color-white-50);
  }

  &.is-primary.is-disabled,
  &.is-primay.is-loading {
    opacity: 0.3;
    filter: saturate(0);
  }

  &.with-chip {
    .content {
      justify-content: normal;

      .label {
        text-align: end;
        flex: 1;
      }
    }
  }

  &.flag {
    padding-left: calc(var(--spacing-xl) + 6px);
  }
}

.gam-button-container.is-disabled,
.gam-button-container.is-loading {
  pointer-events: none;

  .icon {
    opacity: 0.5;
  }
}
</style>
